// @ts-expect-error
import type {Field as ControllerField} from 'react-hook-form';

import {useMutation} from '@apollo/react-hooks';

import {colors} from 'shells/colors/colors';
import {AccountIcon} from 'shells/icon/svg/account-icon';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {LeadIcon} from 'shells/icon/svg/lead-icon';

import type {FormFieldType, EntityType} from 'nutshell-graphql-types';
import type {
    UpdateForm as UpdateFormMutation,
    UpdateFormVariables as UpdateFormMutationVariables,
    GetForm as GetFormQuery,
    FormUpdateInput,
    FormFieldInput,
    FormFieldStylesInput,
    FormFragment_editableVersion as FormVersion,
    FormFragment_editableVersion_fields as GraphQLField,
    FormFragment_publishedVersion_fields_attribute as Attribute,
    FormFragment_editableVersion_fields_rules_objectsToAssign as ObjectToAssign,
} from 'nutshell-graphql-types';

import type {FormFieldSchema} from '../core/nutshell-fields/types';
import {getReportDefaultFields} from '../reports/report-constants';

import FORM_FRAGMENT from './graphql/fragments/form-fragment.graphql';
import FORM_UPDATE from './graphql/mutations/update-form.graphql';
import type {
    Form,
    Field,
    SubmitButtonField,
    EntityTypeFromApi,
    InputFieldStyles,
    SuccessMessageStyles,
    InputFieldType,
    DisplayType,
    EntityTypeOption,
    ElementType,
} from './types';

const FIELD_TYPE_DISPLAY_TYPES = [
    'HEADER',
    'SUBHEADER',
    'TEXT',
    'RADIO',
    'CHECKBOXES',
    'FILE',
    'URL',
    'IMAGE',
];
const INPUT_TYPES = ['INPUT', 'RADIO', 'CHECKBOXES', 'FILE', 'URL'];

// @ts-expect-error
function areFieldTypeAndDisplayTypeEqual(type) {
    return FIELD_TYPE_DISPLAY_TYPES.includes(type);
}

export function isInputType(type: string) {
    return INPUT_TYPES.includes(type);
}

export function translateSchemaEntityTypeToGraphQL(entityType: EntityTypeFromApi): EntityType {
    switch (entityType) {
        case 'Contacts':
            return 'CONTACT';
        case 'Accounts':
            return 'ACCOUNT';
        case 'Leads':
            return 'LEAD';
        default:
            return 'CONTACT';
    }
}

export function translateGraphQLEntityTypeToSchema(entityType: EntityType): EntityTypeFromApi {
    switch (entityType) {
        case 'CONTACT':
            return 'Contacts';
        case 'ACCOUNT':
            return 'Accounts';
        case 'LEAD':
            return 'Leads';
        default:
            return 'Contacts';
    }
}

export const entityTypeOptions: EntityTypeOption[] = [
    {value: 'CONTACT', label: 'Person', icon: ContactIcon},
    {value: 'ACCOUNT', label: 'Company', icon: AccountIcon},
    {value: 'LEAD', label: 'Lead', icon: LeadIcon},
];

// When we create a new input field, we need to check what type to default to. The default
// type for all new fields is INPUT except for the ones specified here.
export function getFieldTypeFromDisplayType(displayType: DisplayType): InputFieldType {
    if (displayType === 'checkboxes') {
        return 'CHECKBOXES';
    }
    if (displayType === 'radio') {
        return 'RADIO';
    }
    if (displayType === 'file') {
        return 'FILE';
    }
    if (displayType === 'url') {
        return 'URL';
    }

    return 'INPUT';
}

/**
 * Determines the address field type based on the field properties.
 *
 * @param {Object} field - The field object.
 * @returns {string} - The address field type.
 */
export function getAddressFieldType(field: Field): DisplayType | null {
    if (field.type === 'INPUT' && field.fieldName === 'address') {
        return field.isSeparateFields ? 'multiLineAddress' : 'singleLineAddress';
    }

    return null;
}

function getDisplayTypeWhenSchemaPropertyMissing(field: Field): string {
    if ('entityType' in field) {
        switch (field.type) {
            case 'DATE':
                return 'date';
            case 'RADIO':
                return 'radio';
            case 'CHECKBOXES':
                return 'checkboxes';
            case 'FILE':
                return 'file';
            case 'URL':
                return 'url';
            case 'IMAGE':
                return 'image';
            case 'INPUT':
            default:
                return 'textfield';
        }
    }
    return 'textfield';
}

export function getFieldDisplayType(schema: FormFieldSchema, field: Field): string {
    const addressFieldType = getAddressFieldType(field);
    if (addressFieldType) {
        return addressFieldType;
    }

    if (areFieldTypeAndDisplayTypeEqual(field.type)) {
        return field.type;
    }

    // @ts-expect-error
    const fieldKey: string = field.attributeId || field.fieldName;

    const property = schema.properties[fieldKey];
    if (property) {
        return property.displayType;
    }

    return getDisplayTypeWhenSchemaPropertyMissing(field);
}

export function convertFormStateToGraphQL(form: Form): FormUpdateInput {
    // @ts-expect-error
    const formFields = form.submitButton ? form.fields.concat(form.submitButton) : form.fields;

    const graphQlForm = {
        id: form.id,
        styles: {
            backgroundColor: form.backgroundColor,
            fontStyle: form.fontStyle
                ? {
                      name: form.fontStyle.name,
                      fontFamily: form.fontStyle.fontFamily,
                      // Could potentially be undefined
                      inheritWhenEmbedded: Boolean(form.fontStyle.inheritWhenEmbedded),
                  }
                : {
                      name: 'Arial',
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      inheritWhenEmbedded: true,
                  },
        },
        fields: formFields.map((field) => {
            return convertFormFieldStateToGraphQL(field, form.inputStyle);
        }),
    } as const;

    return {form: graphQlForm};
}

// The default label for a file upload field is 'Upload a file', but we want to
// indicate that it maps to a File field, so we translate here
export function getFieldMappingLabel(
    schema: FormFieldSchema,
    fieldName: string,
    entityType?: EntityType | null,
    fallbackLabel?: string
): string {
    if (fieldName === 'file') {
        return 'File';
    }

    if (fieldName === 'name' && entityType === 'ACCOUNT') {
        return 'Company name';
    }

    const property = schema.properties[fieldName];
    if (property) {
        return property.label;
    }

    return fallbackLabel ?? fieldName;
}

export function convertFormFieldStateToGraphQL(
    field: Field | SubmitButtonField,
    inputStyle: FormFieldStylesInput
): FormFieldInput {
    if (INPUT_TYPES.includes(field.type)) {
        return {
            id: field.id,
            type: field.type,
            label: field.label,
            // @ts-expect-error
            fieldName: field.fieldName,
            // @ts-expect-error
            attributeId: field.attributeId,
            // @ts-expect-error
            entityName: field.entityType,
            // @ts-expect-error
            placeholder: field.placeholder,
            // @ts-expect-error
            isRequired: field.isRequired,
            styles: inputStyle,
            // @ts-expect-error
            isSeparateFields: field.isSeparateFields,
            // @ts-expect-error
            subfields: field.subfields,
            // @ts-expect-error
            rules: field.rules.map((rule) => ({
                answerChoiceId: rule.answerChoice.id,
                assignmentObjectsApiIds: rule.objectsToAssign.map(
                    (object: ObjectToAssign) => object.id
                ),
            })),
            // @ts-expect-error
            defaultValues: field.defaultValues,
        };
    }

    if (field.type === 'SUBMIT_BUTTON') {
        return {
            id: field.id === 'TEMP_SUBMIT_BUTTON' ? null : field.id,
            type: field.type,
            label: field.label,
            styles: {
                backgroundColor: field.backgroundColor,
                fontSize: field.fontSize,
                fontWeight: field.fontWeight,
                justifyContent: field.justifyContent,
                borderRadius: field.borderRadius,
                color: field.color,
            },
        };
    }

    if (field.type === 'IMAGE') {
        return {
            id: field.id,
            type: field.type,
            label: field.label,
            // @ts-expect-error
            imageUrl: field.imageUrl,
            styles: {
                // @ts-expect-error
                width: field.width,
                // @ts-expect-error
                justifyContent: field.justifyContent,
            },
        };
    }

    return {
        id: field.id,
        type: field.type,
        label: field.label,
        fieldName: null,
        entityName: null,
        styles: {
            color: field.color,
            fontSize: field.fontSize,
            // @ts-expect-error
            fontWeight: field.fontWeight,
            // @ts-expect-error
            textAlign: field.textAlign,
        },
    };
}

// This sets all the input styles to the styles of the first field and then we use
// these as the style values on all the input type fields. We update this value
//  any time any of the input field have a style update to keep the input styles in sync.
function getFormInputStyles(graphQlForm: FormVersion) {
    const inputField = graphQlForm.fields.find((field) => isInputType(field.type));
    const inputStyle = inputField
        ? JSON.parse(inputField.styles)
        : {
              color: '#4d4540',
              border: '.5px solid #d4d1cf',
              borderRadius: 3,
              fontSize: 13,
          };

    return inputStyle;
}

export function convertGraphQLResponseToFormState(graphQlForm: FormVersion): Form {
    const formStyles = graphQlForm.styles === '' ? {} : JSON.parse(graphQlForm.styles);
    const submitButtonField = graphQlForm.fields.find((field) => field.type === 'SUBMIT_BUTTON');
    const fields = graphQlForm.fields.filter((field) => field.type !== 'SUBMIT_BUTTON');

    const form = {
        id: graphQlForm.id,
        backgroundColor: formStyles.backgroundColor,
        fontStyle: formStyles.fontStyle,
        inputStyle: getFormInputStyles(graphQlForm),
        fields: fields.map((field) => {
            return convertGraphQLResponseToFormFieldState(field);
        }),
        submitButton: submitButtonField
            ? convertGraphQLResponseToFormFieldState(submitButtonField)
            : undefined,
    } as const;

    // @ts-expect-error
    return form;
}

export function convertGraphQLResponseToFormFieldState(
    field: GraphQLField
): Field | SubmitButtonField {
    const fieldStyles = JSON.parse(field.styles);
    const subfields = JSON.parse(field.subfields);
    const isSeparateFields = field.isSeparateFields;

    return {
        id: field.id,
        type: field.type,
        fieldName: field.fieldName,
        label: field.label,
        // @ts-expect-error
        entityType: field.entityName,
        entityTypes: field.entityTypes,
        // @ts-expect-error
        placeholder: field.placeholder,
        border: fieldStyles.border,
        borderRadius: fieldStyles.borderRadius,
        fontSize: fieldStyles.fontSize,
        fontWeight: fieldStyles.fontWeight,
        textAlign: fieldStyles.textAlign,
        justifyContent: fieldStyles.justifyContent,
        color: fieldStyles.color,
        inputColor: fieldStyles.inputColor ? fieldStyles.inputColor : fieldStyles.color,
        backgroundColor: fieldStyles.backgroundColor,
        attributeId: field.attribute ? field.attribute.id : null,
        isRequired: field.isRequired,
        isRemovable: field.isRemovable,
        width: fieldStyles.width,
        imageUrl: field.image ? field.image.imageUrl : null,
        subfields: subfields,
        isSeparateFields,
        rules: field.rules,
        defaultValues: field.defaultValues,
    };
}

export function confineValueToRange(
    value: number,
    min: number,
    max: number,
    defaultValue: number
): number {
    if (!value) return defaultValue;

    return Math.min(Math.max(value, min), max);
}

// Returns just the color hex code from a border style string
export function getBorderColorFromBorder(border: string): string {
    const borderStyles = border.split(' ');

    // Use the default color as a fallback
    let borderColor: string = colors.formBorder;

    borderStyles.forEach((style) => {
        if (style.startsWith('#')) {
            borderColor = style.trim();
        }
    });

    return borderColor;
}

// Returns an updated border string with new color
export function updateBorderColorForBorder(border: string, newBorderColor: string): string {
    const borderStyles = border.split(' ');

    let updatedStyle = '';

    borderStyles.forEach((style) => {
        if (style.startsWith('#')) {
            // Replace the old color with the new one
            updatedStyle = updatedStyle.concat(newBorderColor, ' ');
        } else {
            // Keep existing unchanged styles
            updatedStyle = updatedStyle.concat(style, ' ');
        }
    });

    return updatedStyle.trim();
}

export function useFormUpdate(): (arg1: FormUpdateInput) => Promise<any> {
    const [updateForm] = useMutation<UpdateFormMutation, UpdateFormMutationVariables>(FORM_UPDATE);

    return (input: FormUpdateInput) => {
        return updateForm({
            variables: {input},
            update: (proxy, mutationResults) => {
                const cacheResult: GetFormQuery | null | undefined = proxy.readFragment({
                    id: input.form.id,
                    fragmentName: 'FormFragment',
                    fragment: FORM_FRAGMENT,
                });

                if (!cacheResult) {
                    return;
                }

                const updatedForm =
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.formUpdate &&
                    mutationResults.data.formUpdate.form;
                if (!updatedForm) return;

                proxy.writeFragment({
                    id: input.form.id,
                    fragmentName: 'FormFragment',
                    fragment: FORM_FRAGMENT,
                    data: updatedForm,
                });
            },
        });
    };
}

export function hexToRgba(hex: string, alpha?: number): string {
    let r = 0;
    let g = 0;
    let b = 0;

    if (hex.length === 7) {
        r = parseInt(hex.slice(1, 3), 16);
        g = parseInt(hex.slice(3, 5), 16);
        b = parseInt(hex.slice(5, 7), 16);
    }

    if (hex.length === 4) {
        r = parseInt(hex.slice(1, 2), 16);
        g = parseInt(hex.slice(2, 3), 16);
        b = parseInt(hex.slice(3, 4), 16);
    }

    if (alpha) {
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    } else {
        return `rgb(${r}, ${g}, ${b})`;
    }
}

export function getInputStyles(styles: {
    borderRadius: number;
    border: string;
    fontSize: number;
    color: string;
    inputColor?: string;
}): InputFieldStyles {
    return {
        borderRadius: `${styles.borderRadius}px`,
        border: styles.border,
        fontSize: `${styles.fontSize}px`,
        color: styles.inputColor ? styles.inputColor : hexToRgba(styles.color, 0.6),
        height: 'unset',
        padding: '8px',
    };
}

export function getSuccessMessageStyles(styles: {
    fontFamily: string;
    fontSize: number;
    color: string;
}): SuccessMessageStyles {
    return {
        fontFamily: styles.fontFamily,
        fontSize: `${styles.fontSize}px`,
        color: hexToRgba(styles.color),
    };
}

// This pared-down type allows us to use getDefaultFieldsForSubmissionsTable for
// forms on the form page OR wfForms on the connected forms page
type PublishedField = {
    type: FormFieldType;
    fieldName: string | null | undefined;
    entityName: string | null | undefined;
    attribute?: Attribute | null | undefined;
};

export function getDefaultFieldsForSubmissionsTable(fields?: PublishedField[] | null): string {
    const defaultFields = getReportDefaultFields('formEngagement').split(',');

    if (!fields) {
        return defaultFields.join(',');
    }

    const inputFields = fields.filter((field) => isInputType(field.type));

    inputFields.forEach((field) => {
        if (field.fieldName && field.fieldName === 'name' && field.entityName === 'ACCOUNT') {
            defaultFields.push('accounts');
        } else if (
            field.fieldName &&
            field.fieldName === 'address' &&
            field.entityName === 'ACCOUNT'
        ) {
            defaultFields.push('companyAddress');
        } else if (
            field.fieldName &&
            field.fieldName === 'phone' &&
            field.entityName === 'ACCOUNT'
        ) {
            defaultFields.push('companyPhone');
        } else if (field.fieldName && field.entityName === 'CONTACT') {
            if (field.fieldName && !defaultFields.includes(field.fieldName)) {
                defaultFields.push(field.fieldName);
            }
        } else if (field.attribute) {
            defaultFields.push(field.attribute.name);
        }
    });

    return defaultFields.join(',');
}

export function getDefaultStateFromFormFields(fields: string) {
    return {
        search: `?filter%5Bperiod%5D=-d30&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&&report%5Bgap%5D=d&report%5Bsegment%5D=interactionType&list%5Bfields%5D%5Bcontacts%5D=${encodeURIComponent(
            fields
        )}&list%5Bpage%5D=1&list%5Bsort%5D=-createdTime`,
    };
}

const inputElementsFocusableOnClick = ['INPUT', 'TEXTAREA'];

export function getElementToFocusFromClick(e: {
    target: EventTarget;
}): ElementType | null | undefined {
    const target = e.target as Element;
    const clickedNode = target.nodeName;

    let element = null;
    // editable input elements can be type input or textarea for now
    if (inputElementsFocusableOnClick.includes(clickedNode)) {
        element = 'input';
    }

    // label fields are button components when not in edit mode
    if (clickedNode === 'BUTTON') {
        element = 'label';
    }

    const focusElement = target.getAttribute('data-focus-element');
    if (focusElement && (focusElement === 'input' || focusElement === 'label')) {
        element = focusElement;
    }

    // @ts-expect-error
    return element;
}

export function handleFileUpload(field: ControllerField, id: string) {
    const filepickerClient = Nut.modules.Global.init.configureFilePicker();

    const filestackOptions = {
        maxSize: 52428800,
        uploadInBackground: false,
        fromSources: [
            'local_file_system',
            // TODO: Re-enable cloud sources once Filestack integration issues are resolved
            // 'box',
            // 'dropbox',
            // 'googledrive',
            // 'imagesearch',
            'webcam',
            'url',
        ],
        // @ts-expect-error
        onUploadDone(newFiles) {
            return $.ajax({
                type: 'POST',
                url: `/rest/forms/${id}/files`,
                contentType: 'application/json',
                data: JSON.stringify(newFiles.filesUploaded[0]),
            }).then(() => {
                const newFileUrl = newFiles.filesUploaded[0].url;
                field.onChange(newFileUrl);
            });
        },
        // @ts-expect-error
        onFileUploadFailed(file, error) {
            // eslint-disable-next-line no-console
            console.error('FilePicker error', error, 'for file', file);
            // @ts-expect-error
            if (typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.track(
                    `FilePicker error: ${error.message} -- ${error.details} -- ${error.stack}`
                );
            }
        },
    } as const;

    try {
        filepickerClient.picker(filestackOptions).open();
    } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error(error.message, error.details);
        // @ts-expect-error
        if (typeof window.trackJs !== 'undefined') {
            // @ts-expect-error
            window.trackJs.track(`${error.message}: ${JSON.stringify(error.details)}`);
        }
    }
}
